import { useEffect, useState } from 'react';


import useHttp from '../../../hooks/useHTTP';
import { ContentSearchRequest, Search, SearchFilters, SearchResult } from '../interfaces/Search';
import { getAppGuid, toSentenceCase } from '../utility';

const useSearch = () => {

    const [lastFilters, setLastFilters] = useState<SearchFilters>({
        searchTerm: '',
        typeFilter: [],
        dateFilter: '',
        languageFilter: [],
        rulesFilter: ['CS_Published', 'CS_Draft'],
    })
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [currentSkip, setCurrentSkip] = useState<number>(0);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [searchActive, setSearchActive] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { get } = useHttp();

    useEffect(() => {
        if(!lastFilters.searchTerm && searchActive) {
            clearSearch();
        }
    }, [lastFilters.searchTerm])


    function setLastSearchTerms(lastFilters: ContentSearchRequest) {

        if (lastFilters ) {
            const lastRules = lastFilters?.rules ? lastFilters.rules.map(rule => `${rule[0].toLocaleUpperCase()}${rule.slice(1,undefined)}`) : []
            const lastContentStatus = lastFilters?.contentStatuses?.length ? lastFilters.contentStatuses.map(status => `CS_${toSentenceCase(status)}`) : ['CS_Published', 'CS_Draft']
            const rulesFilter = [ ...lastRules, ...lastContentStatus ]

            setLastFilters({
                searchTerm: lastFilters.searchTerm ? lastFilters.searchTerm : '',
                typeFilter: lastFilters.mediaKindFilters ? lastFilters.mediaKindFilters : [],
                dateFilter: lastFilters.since ? getFilterFromOffset(new Date(lastFilters.since)) : undefined,
                languageFilter: lastFilters.languageFilters ? lastFilters.languageFilters : ['CS_Published', 'CS_Draft'],
                rulesFilter
            });
        }
    }

    function getFilterFromOffset(date: Date){
        const daysSince = Math.round(Date.now() - date.getTime()) / (1000 * 60 * 60 * 24)
   
        if (daysSince >= 364) {
            return 'Past Year'
        }
        if (daysSince >= 186) {
            return 'Last 6 Months'
        }
        if (daysSince >= 31) {
            return 'Last 30 Days'
        }

        if(daysSince >= 7) {
            return 'Last 7 Days'
        }
        return undefined;
    }
    
    
    function getDateFilerAsOFfSetTime(dateFilterValue: string) :  Date | undefined {
        var date = new Date();
        switch (dateFilterValue) {
            case 'Last 7 Days':
                date.setDate(new Date().getDate() -7);
                break;
            case 'Last 30 Days':
                date.setDate(new Date().getDate() -31);
                break;
            case 'Last 6 Months':
                date.setDate(new Date().getDate() -186);
                break;
            case 'Past Year':
                date.setDate(new Date().getDate() -365);
                break;
            default:
                return undefined
        }
        return new Date(date.toDateString());
    }

    function getResults(searchTerm: string, typeFilter: string[], dateFilter:string, languageFilter: string[], rulesFilter: string[]){
        if(!searchTerm) {
            clearSearch()
            return;
        }

        if (isLoading) {
            return;
        }

        setSearchActive(true);

        let params = formatAsParams(searchTerm, typeFilter, dateFilter, languageFilter, rulesFilter)

        setLastFilters({searchTerm, typeFilter, dateFilter, languageFilter, rulesFilter})
        setIsLoading(true);

        get(`/search/app/${getAppGuid()}/content`, {params: {...params, boostedIetfLanguageTag:window.navigator.language}})
            .then(({data}: {data:Search}) => {
                setSearchResults(data.items)
                setCurrentSkip(data.nextSkip)
                setTotalResults(data.totalItemCount)
            })
            .catch(err => {
                console.log(err)
                setSearchResults([])
                setCurrentSkip(0)
                setTotalResults(0)
            })
            .finally(() => {
                setIsLoading(false);
            })
        
        setSearchActive(true);
    }

    function formatAsParams(searchTerm: string, typeFilter: string[], dateFilter:string, languageFilter: string[], rulesFilter: string[]) {
        let params = {};
        if (searchTerm) {
            params = {SearchTerm: searchTerm}

            if (typeFilter.length) {
                let mediaKind = {};
                typeFilter.forEach((el, index) => {
                    mediaKind[`MediaKindFilters[${index}]`as any] = el
                })
                params = {...params, ...mediaKind}
            }

            if (dateFilter && getDateFilerAsOFfSetTime(dateFilter)) {
                params = {...params, Since: new Date(getDateFilerAsOFfSetTime(dateFilter)).toJSON()}
            }

            if (languageFilter.length) {
                let languages= {};
                languageFilter.forEach((el, index) => {
                    languages[`LanguageFilters[${index}]`] = el
                })
                params = {...params, ...languages}
            }
            if(rulesFilter.length) {
                let rules= {};
                rulesFilter.filter(f=>!f.startsWith("CS_")).forEach((el, index) => {
                    rules[`Rules[${index}]`] = el
                })
                rulesFilter.filter(f=>f.startsWith("CS_")).forEach((el, index) => {
                    rules[`ContentStatuses[${index}]`] = el.replace("CS_", "")
                })
                params = {...params, ...rules}
            }
        }

        return params;
    }

    function getMore(){
        
        setIsLoading(true);

        get(`/search/app/${getAppGuid()}/content`, {params: {...formatAsParams(lastFilters.searchTerm, lastFilters.typeFilter, lastFilters.dateFilter, lastFilters.languageFilter, lastFilters.rulesFilter), boostedIetfLanguageTag:window.navigator.language, skip:currentSkip}})
            .then(({data}: {data:Search}) => {
                setSearchResults([...searchResults, ...data.items])
                setCurrentSkip(data.nextSkip)
                setTotalResults(data.totalItemCount)
            })
            .catch(err => {
                console.log(err)
                setSearchResults([])
                setCurrentSkip(0)
                setTotalResults(0)
            })
            .finally(() => {
                setIsLoading(false);
            })
        
        setSearchActive(true);
    }

    function clearFilters(){
        setLastFilters({
            searchTerm: lastFilters.searchTerm,
            typeFilter: [],
            dateFilter: '',
            languageFilter: lastFilters.languageFilter,
            rulesFilter: ['CS_Published', 'CS_Draft']
        });
    }

    function clearSearch() {
        setSearchResults([])
        setCurrentSkip(0)
        setTotalResults(0)
        setSearchActive(false);
        setLastFilters({
            searchTerm: '',
            typeFilter: [],
            dateFilter: '',
            languageFilter: lastFilters.languageFilter,
            rulesFilter: lastFilters.rulesFilter
        });
    }

    return {searchResults, currentSkip, totalResults, isLoading, searchActive, getResults, lastFilters, workspace: '', clearSearch, searchCompleted: searchActive, setLastFilters, getAppGuid, getMore, clearFilters, setLastSearchTerms}
}

export default useSearch;